import axios from 'axios'
import { PackageType, CreatePackageTypeRequest, UpdatePackageTypeRequest, BulkPrintRequest, QRCodeResponse, PackageAnalytics } from '@/types/packageTypes'

export class PackageTypeService {
  private baseUrl = '/api/settings/package-types'

  async getPackageTypes(): Promise<PackageType[]> {
    const response = await axios.get<{ data: PackageType[], pagination: any }>(this.baseUrl)
    // Handle both old array format and new object format for backwards compatibility
    if (Array.isArray(response.data)) {
      return response.data
    }
    return response.data.data || []
  }

  async getPackageType(id: number): Promise<PackageType> {
    const response = await axios.get<PackageType>(`${this.baseUrl}/${id}`)
    return response.data
  }

  async createPackageType(data: CreatePackageTypeRequest): Promise<PackageType> {
    const response = await axios.post<PackageType>(this.baseUrl, data)
    return response.data
  }

  async updatePackageType(id: number, data: UpdatePackageTypeRequest): Promise<PackageType> {
    const response = await axios.put<PackageType>(`${this.baseUrl}/${id}`, data)
    return response.data
  }

  async deletePackageType(id: number): Promise<void> {
    await axios.delete(`${this.baseUrl}/${id}`)
  }

  async generateQRCode(id: number, format: 'png' | 'base64' = 'png'): Promise<QRCodeResponse> {
    const response = await axios.post<QRCodeResponse>(`${this.baseUrl}/${id}/qr-code`, {
      format: format.toUpperCase(),
      size: 300,
      includeLabel: true
    })
    return response.data
  }

  async getQRCodeImage(id: number): Promise<string> {
    const response = await axios.get<string>(`${this.baseUrl}/${id}/qr-code`)
    return response.data
  }

  async bulkPrintQRCodes(request: BulkPrintRequest): Promise<any> {
    const response = await axios.post(`${this.baseUrl}/bulk-print`, request)
    return response.data
  }

  async logUsage(id: number, context: string, data?: any): Promise<any> {
    const response = await axios.post(`${this.baseUrl}/${id}/usage`, {
      context,
      ...data
    })
    return response.data
  }

  async getAnalytics(from?: Date, to?: Date): Promise<PackageAnalytics> {
    const params: any = {}
    if (from) params.from = from.toISOString()
    if (to) params.to = to.toISOString()
    
    const response = await axios.get<PackageAnalytics>(`${this.baseUrl}/analytics`, { params })
    return response.data
  }

  async checkNameExists(name: string): Promise<boolean> {
    try {
      const packages = await this.getPackageTypes()
      return packages.some(p => p.name.toLowerCase() === name.toLowerCase())
    } catch (error) {
      console.error('Failed to check package name:', error)
      return false
    }
  }

  // Parse QR code data from scanner
  parseQRCodeData(qrData: string): any {
    try {
      const data = JSON.parse(qrData)
      if (data.type !== 'package') {
        throw new Error('Invalid QR code type')
      }
      return data
    } catch (error) {
      console.error('Failed to parse QR code:', error)
      throw new Error('Invalid QR code data')
    }
  }

  // Get package from QR code
  async getPackageFromQRCode(qrData: string): Promise<PackageType | null> {
    try {
      const parsed = this.parseQRCodeData(qrData)
      if (parsed?.id) {
        return await this.getPackageType(parsed.id)
      }
      return null
    } catch (error) {
      console.error('Failed to get package from QR code:', error)
      return null
    }
  }
}